import { useState } from 'react';

import { deliveriesApi } from '@/api/deliveries.api';
import type { DeliveryDetail } from '@/api/types';
import { errorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { Textarea } from './ui/field';
import { Sheet, SheetContent } from './ui/sheet';

/**
 * Giving up on a delivery.
 *
 * Reachable only once the medicine is in hand: before pickup the state machine
 * has no edge to FAILED, because nothing has changed hands and the job simply
 * goes back to dispatch to reassign. Offering the button earlier would be
 * offering a 409.
 *
 * A reason is mandatory. A failed delivery is a bag of medicine that has to
 * get back to the pharmacy and a customer who has to be phoned, and "failed"
 * on its own tells the office neither.
 */
export function FailSheet({
  deliveryId,
  open,
  onOpenChange,
  onFailed,
}: {
  deliveryId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFailed: (updated: DeliveryDetail) => void;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [other, setOther] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const reason = (chosen ?? other).trim();
  // Three characters is the backend's own minimum; enforcing it here means the
  // rider is told before the round trip rather than after it.
  const ready = reason.length >= 3;

  const reset = () => {
    setChosen(null);
    setOther('');
    setError(null);
  };

  async function submit() {
    if (working || !ready) return;
    setWorking(true);
    setError(null);
    try {
      const updated = await deliveriesApi.fail(deliveryId, reason);
      onFailed(updated);
      onOpenChange(false);
      reset();
    } catch (failError) {
      setError(errorMessage(failError));
    } finally {
      setWorking(false);
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <SheetContent
        title="What went wrong?"
        description="Take the bag back to the pharmacy. The office will call the customer."
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          className="grid gap-4"
        >
          <fieldset className="grid gap-2">
            <legend className="sr-only-text">Pick a reason</legend>
            <div className="flex flex-wrap gap-2">
              {REASONS.map((option) => {
                const selected = chosen === option;
                return (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => {
                      // Picking a chip clears the free text, so the sheet
                      // cannot end up sending one thing while showing another.
                      setChosen(selected ? null : option);
                      if (!selected) setOther('');
                      setError(null);
                    }}
                    className={cn(
                      'min-h-tap rounded-full border px-4 py-2 text-sm font-medium',
                      'transition-colors duration-[var(--motion-duration-fast)] ease-standard',
                      'forced-outline',
                      selected
                        ? 'border-line-brand bg-wash text-brand'
                        : 'border-line-strong bg-raised text-ink-muted hover:border-line-bold hover:bg-sunken',
                    )}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="grid gap-1.5">
            <label htmlFor="fail-other" className="text-sm font-medium text-ink">
              Or say what happened
            </label>
            <Textarea
              id="fail-other"
              maxLength={300}
              rows={3}
              value={other}
              onChange={(event) => {
                setOther(event.target.value);
                // Typing is a choice too.
                if (event.target.value.trim().length > 0) setChosen(null);
                setError(null);
              }}
              placeholder="Anything the office needs to know"
            />
          </div>

          {error ? (
            <p role="alert" className="text-sm font-medium text-error-ink">
              {error}
            </p>
          ) : null}

          {/*
           * Destructive, so `danger` — the same variant as the trigger that
           * opened this sheet. Red in one place and brand-green in the other
           * would be a bug.
           */}
          <Button
            type="submit"
            variant="danger"
            size="lg"
            block
            disabled={!ready}
            loading={working}
            loadingLabel="Recording"
          >
            Mark as not delivered
          </Button>

          <Button type="button" variant="ghost" size="md" block onClick={() => onOpenChange(false)}>
            Keep trying
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

/**
 * The reasons that actually happen, so the office gets a consistent word for
 * each rather than twelve spellings of "nobody home". Free text stays, because
 * the thirteenth case always exists.
 */
const REASONS = [
  'Nobody at the address',
  'Customer refused the order',
  'Could not find the address',
  'Customer asked to reschedule',
  'Customer could not pay',
];
